import { logger } from '@/utils/logger/logger';

export const TRANSCRIPT_HIGHLIGHT_ID = 'transcript-highlight';
export const TRANSCRIPT_HIGHLIGHT_SELECTOR = '[data-transcript-highlight]';

const MAX_WAIT_MS = 5000;
const POLL_INTERVAL_MS = 50;

const scrollableOverflow = (value: string) =>
  value === 'auto' || value === 'scroll' || value === 'overlay';

/** Scrolls every overflow ancestor so nested transcript panes reveal the target. */
export function scrollTranscriptHighlightElement(element: HTMLElement): void {
  const scrollParents: HTMLElement[] = [];
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (scrollableOverflow(overflowY)) {
      scrollParents.push(parent);
    }
    parent = parent.parentElement;
  }

  for (const container of scrollParents) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const nextScrollTop =
      container.scrollTop +
      (elementRect.top - containerRect.top) -
      containerRect.height / 2 +
      elementRect.height / 2;

    container.scrollTo({ top: Math.max(0, nextScrollTop), behavior: 'smooth' });
  }

  try {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch (error) {
    logger.error('Error scrolling to transcript highlight:', error);
  }
}

function findTranscriptHighlightAnchor(): HTMLElement | null {
  return document.querySelector<HTMLElement>(TRANSCRIPT_HIGHLIGHT_SELECTOR);
}

/**
 * Scrolls to the first transcript evidence highlight once it appears in the DOM.
 * Module-level scheduling survives tab switches that unmount the caller's hook.
 */
export function scrollToTranscriptHighlight(): void {
  const deadline = Date.now() + MAX_WAIT_MS;

  const attempt = () => {
    const target = findTranscriptHighlightAnchor();
    if (target) {
      scrollTranscriptHighlightElement(target);
      return;
    }
    if (Date.now() < deadline) {
      setTimeout(attempt, POLL_INTERVAL_MS);
    }
  };

  setTimeout(attempt, POLL_INTERVAL_MS);
}
